import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import * as S from './styled/RangeSlider.styled';

/**
 * RangeSlider component allows users to select a range of values within a specified min and max.
 *
 * @param {number} min - The minimum value of the slider.
 * @param {number} max - The maximum value of the slider.
 * @param {number} [step=1] - The step value for the slider.
 * @param {number[]} value - The current value of the slider as an array [leftValue, rightValue].
 * @param {function} onChange - Callback function to handle value changes.
 * @param {boolean} disabledMin - Flag to disable the left thumb.
 * @param {boolean} disabledMax - Flag to disable the right thumb.
 * @param {string} className - Additional class name to apply to the slider.
 * @param {boolean} isOverlap - Flag to allow the thumbs to overlap.
 * @returns {JSX.Element} The rendered RangeSlider component.
 */
const RangeSlider = ({
	min,
	max,
	step = 1,
	value,
	onChange,
	disabledMin,
	disabledMax,
	className,
	isOverlap = false
}) => {
	const isRtl = useSelector((state) => state.layout.localization.dir) === 'rtl';
	const [leftValue, setLeftValue] = useState(value[0]);
	const [rightValue, setRightValue] = useState(value[1]);
	const [dragging, setDragging] = useState(null); // State to track which thumb is being dragged
	const sliderRef = useRef(null); // Reference to the slider DOM element

	/**
	 * Update the slider values when the value prop changes
	 */
	useEffect(() => {
		const newLeftValue = Math.max(min, Math.min(value[0], isOverlap ? max : rightValue - step));
		const newRightValue = Math.min(max, Math.max(value[1], isOverlap ? min : leftValue + step));

		if (newLeftValue !== leftValue || newRightValue !== rightValue) {
			setLeftValue(newLeftValue);
			setRightValue(newRightValue);
		}
	}, [value, min, max, leftValue, rightValue, isOverlap]);

	/**
	 * Handle mouse up event to stop dragging
	 */
	const handleMouseUp = useCallback(() => {
		setDragging(null);
	}, []);

	/**
	 * Handle mouse move event to update the slider values while dragging
	 * @param {MouseEvent} e - The mouse move event
	 */
	const handleMouseMove = useCallback(
		(e) => {
			if (dragging && sliderRef.current) {
				const rect = sliderRef.current.getBoundingClientRect();
				const offsetX = isRtl ? rect.right - e.clientX : e.clientX - rect.left;
				const percent = (offsetX / rect.width) * (max - min) + min;

				let newValue;

				if (dragging === 'left') {
					newValue = Math.round((percent - min) / step) * step + min;
					newValue = Math.min(Math.max(newValue, min), isOverlap ? max : rightValue - step);
					if (newValue < rightValue || isOverlap) {
						setLeftValue(newValue);
						onChange([newValue, rightValue]);
					}
				} else if (dragging === 'right') {
					newValue = Math.round((percent - min) / step) * step + min;
					newValue = Math.max(Math.min(newValue, max), isOverlap ? min : leftValue + step);
					if (newValue > leftValue || isOverlap) {
						setRightValue(newValue);
						onChange([leftValue, newValue]);
					}
				}
			}
		},
		[dragging, leftValue, rightValue, max, min, step, onChange, isOverlap, isRtl]
	);

	/**
	 * Handle click event to update the slider values
	 * @param {MouseEvent} e - The click event
	 */
	const handleClick = useCallback(
		(e) => {
			const rect = sliderRef.current.getBoundingClientRect();
			const offsetX = isRtl ? rect.right - e.clientX : e.clientX - rect.left;
			const percent = (offsetX / rect.width) * (max - min) + min;

			const newValue = Math.round((percent - min) / step) * step + min;

			if (!disabledMin && !disabledMax) {
				if (Math.abs(newValue - leftValue) < Math.abs(newValue - rightValue)) {
					if (newValue < rightValue - step || isOverlap) {
						setLeftValue(newValue);
						onChange([newValue, rightValue]);
					}
				} else {
					if (newValue > leftValue + step || isOverlap) {
						setRightValue(newValue);
						onChange([leftValue, newValue]);
					}
				}
			} else if (disabledMin && !disabledMax) {
				const updatedRightValue = Math.round((percent - min) / step) * step + min;
				setRightValue(updatedRightValue);
				onChange([leftValue, updatedRightValue]);
			} else if (!disabledMin && disabledMax) {
				const updatedLeftValue = Math.round((percent - min) / step) * step + min;
				setLeftValue(updatedLeftValue);
				onChange([updatedLeftValue, rightValue]);
			}
		},
		[leftValue, rightValue, disabledMin, disabledMax, min, max, step, onChange, isOverlap, isRtl]
	);

	/**
	 * Add event listeners for mouse move and mouse up events
	 */
	useEffect(() => {
		window.addEventListener('mousemove', handleMouseMove);
		window.addEventListener('mouseup', handleMouseUp);
		return () => {
			window.removeEventListener('mousemove', handleMouseMove);
			window.removeEventListener('mouseup', handleMouseUp);
		};
	}, [handleMouseMove, handleMouseUp]);

	return (
		<S.SliderContainer
			className={`range-slider ${className ? className : ''}`}
			ref={sliderRef}
			onClick={handleClick}>
			<S.Track
				left={((leftValue - min) / (max - min)) * 100}
				right={100 - ((rightValue - min) / (max - min)) * 100}
			/>
			<S.Thumb
				disabled={disabledMin}
				left={((leftValue - min) / (max - min)) * 100}
				onMouseDown={disabledMin ? undefined : () => setDragging('left')}
			/>
			<S.Thumb
				disabled={disabledMax}
				left={((rightValue - min) / (max - min)) * 100}
				onMouseDown={disabledMax ? undefined : () => setDragging('right')}
			/>
		</S.SliderContainer>
	);
};

export default RangeSlider;
